import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNetworkState } from 'react-use';

import { openUploadModal } from 'components/Layouts/Form/FormBuilder/Content/AddNewContent/uploadModal';
import { defaultValue } from 'components/shared/LandingPage/contexts/LandingPageContext';
import {
  OnAddBlockInsideSpoiler,
  OnDeleteBlockInsideSpoiler,
  OnUpdateBlockInsideSpoiler,
} from 'components/shared/LandingPage/contexts/types';
import { ITINERARIES_ITEM_TYPES } from 'constants/index';
import useAssetsPermissions from 'hooks/useAssetsPermissions';
import {
  editLandingBlockThunk,
  removeLandingBlockThunk,
  editLandingSectionTitleThunk,
  editLandingPageThunk,
  addLandingBlockThunk,
  addLandingBlockInsideAccordionThunk,
  editLandingBlockInsideAccordionThunk,
  removeLandingBlockInsideAccordionThunk,
} from 'store/itinerariesItem/thunks';
import { accountIdSelector } from 'store/user/selectors';
import axiosApi from 'utils/axios';
import { debounce } from 'utils/debounce';
import { handleNestedAccordion } from 'utils/nestedAccordions';

import { IUseLandingContextProps, IText, IBuilderBlock, IBuilderPage, ILandingData } from './types';
import useUpdatePrimaryPhoto from './useUpdatePrimaryPhoto';

export function useLandingContext({
  landingData,
  page,
  links = [],
  screens = [],
  pageIndex,
  landingPageType,
  readOnly = false,
  isSamplePreview = false,
  isInsideBuilder = false,
  isPrivate = false,
  manageHideCostsPermission = false,
  landingTheme,
  virtualized,
}: IUseLandingContextProps) {
  const dispatch = useDispatch();
  const itinerariesItemType = page.type;
  const { online } = useNetworkState();

  const onReplaceCover = useReplaceCoverHandler({ pageIndex, page, landingData, landingPageType });

  const onUpdateBlock = useCallback(
    (blockIndex: number) =>
      debounce((newObject: IBuilderBlock) => {
        dispatch(
          editLandingBlockThunk({
            pageIndex: pageIndex,
            blockIndex,
            newObject,
          }),
        );
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onAddBlock = useCallback(
    (blockIndex: number) => (newObject: IBuilderBlock) => {
      dispatch(
        addLandingBlockThunk({
          pageIndex: pageIndex,
          blockIndex,
          newObject,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onDeleteBlock = useCallback((blockIndex: number) => {
    return () => {
      dispatch(
        removeLandingBlockThunk({
          pageIndex: pageIndex,
          blockIndex,
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangePageTitle = useCallback(
    debounce((title: string) => {
      const data = {
        pageIndex,
        title,
      };
      dispatch(editLandingSectionTitleThunk(data));
    }, 300),
    [landingData],
  );

  const onChangeLeftSideText = useCallback(
    debounce((text: IText) => {
      dispatch(editLandingPageThunk({ pageIndex, newObject: { ...text } }));
    }, 300),
    [],
  );

  const isInviteType = isPrivate || (isInsideBuilder && itinerariesItemType === ITINERARIES_ITEM_TYPES.GUEST_PORTAL);
  const icons = [
    ...(online || !isInviteType
      ? [
          {
            type: isInviteType ? 'invite' : 'share',
            onClick: () => {},
          },
        ]
      : []),
    {
      type: 'print',
      onClick: () => {},
    },
    {
      type: 'phone',
      onClick: () => {},
    },
    // {
    //   type: 'help',
    //   onClick: () => { },
    // },
  ];

  const onAddBlockInsideSpoiler = useCallback<OnAddBlockInsideSpoiler>(
    ({ spoilerIndex, blockIndex }) =>
      (spoilerBlockIndex, insertPosition) =>
      (newObject) => {
        const handledData = handleNestedAccordion(newObject);
        if (!handledData) return;

        dispatch(
          addLandingBlockInsideAccordionThunk({
            pageIndex,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
            newObject: handledData,
            insertPosition,
          }),
        );
      },
    [],
  );

  const onUpdateBlockInsideSpoiler = useCallback<OnUpdateBlockInsideSpoiler>(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex) =>
      (newObject) => {
        dispatch(
          editLandingBlockInsideAccordionThunk({
            pageIndex,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
            newObject,
          }),
        );
      },
    [],
  );

  const onDeleteBlockInsideSpoiler = useCallback<OnDeleteBlockInsideSpoiler>(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex) =>
      () =>
        dispatch(
          removeLandingBlockInsideAccordionThunk({
            pageIndex,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
          }),
        ),
    [],
  );

  return {
    ...defaultValue,
    landingData,
    landingPageType,
    page,
    links,
    readOnly: readOnly,
    hasHeader: true,
    onChangePageTitle,
    onReplaceCover,
    onUpdateBlock,
    onAddBlock,
    onAddBlockInsideSpoiler,
    onUpdateBlockInsideSpoiler,
    onDeleteBlockInsideSpoiler,
    onDeleteBlock,
    onChangeLeftSideText,
    screens,
    pageIndex,
    isPortalItinerary: false,
    isItinerariesItem: true,
    isInsideBuilder,
    isSamplePreview,
    itinerariesItemType,
    landingTheme,
    headerOptions: {
      hasUserMenu: false,
      userName: 'Guest',
      logoSrc: page.brandSettings?.logo?.url,
      companyName: page.brandSettings?.companyName,
      icons,
      userMenuActions: [],
    },
    private: isPrivate,
    manageHideCostsPermission,
    virtualized,
  };
}

export function useReplaceCoverHandler({
  pageIndex,
  landingData,
  page,
  landingPageType,
}: {
  pageIndex: number;
  landingData: ILandingData;
  page: IBuilderPage;
  landingPageType: string;
}) {
  const dispatch = useDispatch();
  const accountId = useSelector(accountIdSelector);
  const { withAssetSettings } = useAssetsPermissions();

  const onChangePrimaryPhoto = useUpdatePrimaryPhoto({ pageIndex });

  return useCallback(async () => {
    const id = landingData.primaryImageId;
    const selected = [];

    if (id) {
      try {
        const info = (await axiosApi.get(`/acc/${accountId}/assets/${id}`)).data;
        selected.push(info);
      } catch (e: any) {
        if (e?.errors?.status === 404) {
          window.console.warn("The photo doesn't exist");
        }
      }
    }

    // FIXME: fix types
    // @ts-ignore
    const assets = await openUploadModal(dispatch, {
      withAssetSettings,
      selected,
      type: 'media',
      suggested: landingData,
      isPrimaryPhoto: true,
      multiple: false,
      isItinerariesItem: true,
      itinerariesItemType: page.type,
      itinerariesItemId: page?.id,
      landingPageType,
      modalTitle: selected.length ? 'Replace Photo' : 'Add Photo',
      defaults: {
        name: landingData.name,
        tags: landingData.tags,
      },
    });

    if (assets !== null) {
      onChangePrimaryPhoto({
        asset: assets[0],
        point: assets[0]?.imageCoords,
      });
    }
  }, [accountId, dispatch, landingData, onChangePrimaryPhoto]);
}
